import deleteCustomerApi from '@/api/delete/customer/delete-customer';
import getCustomersApi from '@/api/get/customer/get-customers';
import exportCustomersApi from '@/api/get/customer/export-customers';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import TableRowLogo from '@/components/table/components/logo/table-row-logo.component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { Heading2Text } from '@/utils/global-fonts';
import { useCallback, useState, useEffect } from 'react';
import { FiEdit } from 'react-icons/fi';
import { MdOutlineArrowDownward, MdUpgrade } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Modal from '@/components/modal/modal-component';
import dayjs from 'dayjs';
import Styled from './customers-all-style';
import ImportCustomersWrapper from './import-customers/import-customers-wrapper';

const noWhiteSpace = { whiteSpace: 'nowrap' };

/**
 * Page that shows all Customers in a table format.
 * @returns {JSX.Element} The rendered Customer Management page component.
 */
const CustomersAll = () => {
  const tableId = 'customer-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const [exportingData, setExportingData] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const navigate = useNavigate();
  const {
    data,
    count,
    error,
    loading: isLoading,
    refetch,
  } = useLazyQuery(getCustomersApi);

  const [deleteCustomer, { loading: deleteCustomerLoading }] =
    useMutation(deleteCustomerApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const handleDeleteConfirmation = useCallback(
    (customerData) => {
      const deleteCustomerAndShowSuccess = async () => {
        const customerId = customerData?.original?.id;
        await deleteCustomer(customerId, customerData);
        await refetch();

        Alert({
          icon: 'success',
          text: 'Customer deleted successfully',
        });
      };

      ConfirmationModal({
        title: 'Delete Customer',
        text: `Are you sure you want to delete ${customerData.values.name}?`,
        onConfirm: deleteCustomerAndShowSuccess,
      });
    },
    [deleteCustomer, refetch],
  );

  const launchDeleteConfirmation = useCallback(
    (customerData) => {
      handleDeleteConfirmation(customerData);
    },
    [handleDeleteConfirmation],
  );

  const renderLogoAndUniqueId = ({ row }) =>
    row?.original?.logo && <TableRowLogo src={row.original.logo} />;

  const renderStatusPill = ({ row }) => (
    <StatusIndicator status={row.original.status.toLowerCase()} />
  );

  const renderDateAdded = ({ row }) => {
    if (row.original.created_at) {
      const date = row.original.created_at;
      const formattedDate = dayjs(date).format('DD/MM/YYYY');
      const formattedTime = dayjs(date).format('hh:mm');
      return <div>{`${formattedDate} | ${formattedTime}`}</div>;
    }
    return <div />;
  };

  const handleEdit = useCallback(
    (row) => {
      const customerDetails = row.original;
      navigate(`edit/${customerDetails.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const handleView = useCallback(
    (row) => {
      const customerDetails = row.original;
      navigate(`view/${customerDetails.id}`);
    },
    [navigate],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="customer"
        onEdit={() => handleEdit(row)}
        onDelete={() => handleDelete(row)}
        onView={() => handleView(row)}
        onDuplicate={null}
      />
    ),
    [handleDelete, handleEdit, handleView],
  );

  const filters = [
    {
      header: 'Permission:',
      accessor: 'is_supplier',
      placeholder: 'Please select a Permission',
      type: 'select',
      options: [
        { value: false, text: 'Dealer' },
        { value: true, text: 'Supplier' },
      ],
    },
  ];

  const CustomerColumns = [
    {
      Header: '',
      accessor: 'id',
      search: true,
      id: 'id',
      width: '40px',
      Cell: renderLogoAndUniqueId,
      inputSort: true,
    },
    {
      Header: 'Customer NAME',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'registered address',
      id: 'address',
      accessor: 'address',
      search: true,
    },
    {
      Header: 'CONTACT NUMBER',
      id: 'contact_number',
      accessor: 'contact_number',
      search: true,
    },
    {
      Header: 'Added by user',
      id: 'added_by_user',
      accessor: 'added_by_user',
      search: true,
    },
    {
      Header: 'Date Added',
      id: 'created_at',
      accessor: 'created_at',
      search: true,
      Cell: (props) => renderDateAdded(props),
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: (props) => renderStatusPill(props),
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  const handleExport = useCallback(async () => {
    setExportingData(true);

    // Get current table filters/search from localStorage
    const tablePreferences = localStorage.getItem('tablePreferences');
    let queryString = '';
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];
      if (tableConfigs?.query) {
        queryString = tableConfigs.query.join('&');
      }
    }

    const res = await exportCustomersApi(queryString);
    setExportingData(false);
    if (res?.data) {
      Alert({
        icon: 'success',
        text: 'Customers exported successfully, please check your email inbox.',
      });
    } else {
      Alert({
        icon: 'error',
        text: 'Failed to export customers.',
      });
    }
  }, []);

  return (
    <PageWrapper>
      <Styled.MainPageContainer>
        <Styled.Heading>
          <Heading2Text style={noWhiteSpace}>Customer Management</Heading2Text>
          <PermissionWrapper permissions={['customer.create']}>
            <PrimaryButton
              title="New Customer"
              onClick={() => navigate('new')}
              buttonHeight="40px"
              icon={<FiEdit />}
            />
          </PermissionWrapper>
        </Styled.Heading>
        <Table
          feature="customer"
          onRowEdit={(row) => handleEdit(row)}
          onRowDelete={(row) => handleDelete(row)}
          onRowView={(row) => handleView(row)}
          columns={CustomerColumns}
          data={data ?? []}
          tableOptions={['search', 'columns']}
          id={tableId}
          defaultSort={defaultSort}
          defaultVariables={defaultVariables}
          dataCount={count ?? 0}
          downloadAccessor="customer-list"
          error={error}
          loading={
            deleteCustomerLoading ||
            isLoading ||
            exportingData ||
            fetchingPresets
          }
          filters={filters}
          refetch={refetch}
          getDownloadData={getCustomersApi}
          additionalHeaderButtons={[
            {
              title: 'Import',
              icon: MdUpgrade,
              onClick: () => setOpenImportModal(true),
            },
            {
              title: 'Export',
              icon: MdOutlineArrowDownward,
              onClick: handleExport,
            },
          ]}
        />
        <Modal
          open={openImportModal}
          onClose={() => setOpenImportModal(false)}
          close={() => setOpenImportModal(false)}
          title="Import Customers"
          width="400px"
          padding="0px"
          bodyStyle={{
            padding: '0px',
          }}
        >
          <ImportCustomersWrapper
            setOpenImportModal={setOpenImportModal}
            refetch={refetch}
          />
        </Modal>
      </Styled.MainPageContainer>
    </PageWrapper>
  );
};

export default CustomersAll;
